import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, inject, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { EnumStatusDocumento } from '../../../core/enum/EnumStatusDocumento';
import { Doc, DocumentoRequerimentoEnviadoApi, StatusAnaliseDocumento } from '../../../core/models/documento';
import { Steps } from '../../../shared/components/steps/steps';
import { ArquivoDocumento, UploadDocModal } from '../../../shared/components/upload-doc-modal/upload-doc-modal';
import { LoaderService } from '../../../shared/services/loader-service';
import { ParametrizacaoService } from '../../../shared/services/parametrizacao-service';
import { RequerimentoService } from '../../../shared/services/requerimento-service';
import { RequerimentoState } from '../../../shared/services/requerimento-state';
import { ToastService } from '../../../shared/services/toast-service';
import {
  distribuirErrosDaApi,
  ROTA_PAGINA_INDISPONIVEL,
  rotaDoErroRequerimento,
} from '../../../shared/utils/requerimento-erros';

/** Status do documento na tela: pendente e recusado permitem enviar (de novo) o arquivo. */
export type StatusDocumento = 'pendente' | 'em-analise' | 'concluido' | 'recusado';

/**
 * Status na tela conforme o tp_status_analise devolvido no envio. Depois do envio, P quer dizer que o
 * arquivo chegou e aguarda a análise, por isso também fica em análise.
 */
const STATUS_POR_ANALISE: Record<StatusAnaliseDocumento, StatusDocumento> = {
  P: 'em-analise',
  E: 'em-analise',
  V: 'concluido',
  N: 'recusado',
};

interface SituacaoDocumento {
  /** `id` do catálogo de tipos de documento, enviado junto com o arquivo. */
  idTipoDocumento: number;
  /** `nome` do catálogo de tipos de documento (ex.: CNH_RG). */
  tipoDocumento: string;
  nomeDocumento: string;
  status: StatusDocumento;
  /** Id do envio vigente, usado para removê-lo; ausente enquanto o documento está pendente. */
  idDocumento?: number;
  /** Nome do arquivo enviado; ausente enquanto o documento está pendente. */
  nomeArquivo?: string;
  /** ds_motivo_recusa do último envio, quando o documento foi recusado. */
  motivoRecusa?: string;
}

/** Documento exibido no card. */
interface DocumentoCard extends Doc {
  /** Em análise ou concluído: não permite novo upload. */
  travado: boolean;
  /** Ainda não enviado: upload liberado. */
  pendente: boolean;
  /** Borda vermelha: recusado, ou pendente depois de a pessoa abrir o upload sem enviar o arquivo. */
  semArquivo: boolean;
  /** Lixeira: envio vigente no rascunho (a API só remove documento de requerimento em rascunho). */
  removivel: boolean;
}

/** Status do upload-doc-modal para os documentos travados (borda amarela ou verde). */
const STATUS_CARD: Record<'em-analise' | 'concluido', EnumStatusDocumento> = {
  'em-analise': EnumStatusDocumento.EmAnalise,
  concluido: EnumStatusDocumento.Sucesso,
};

const MENSAGEM_ERRO_ENVIAR = 'Não foi possível enviar os documentos. Tente novamente.';
const MENSAGEM_ERRO_REMOVER = 'Não foi possível remover o documento. Tente novamente.';
const MENSAGEM_RECUSA_PADRAO = 'Documento recusado. Envie um novo arquivo.';

const ROTA_DETALHES = '/requerimento-beneficio/detalhes';

/** Documento que ainda aceita arquivo: não enviado ou recusado. */
function aceitaEnvio(status: StatusDocumento): boolean {
  return status === 'pendente' || status === 'recusado';
}

/** Situação na tela do envio atual do tipo; sem envio, o documento fica pendente. */
function situacaoDoEnvio(
  enviado: DocumentoRequerimentoEnviadoApi | undefined,
): Pick<SituacaoDocumento, 'status' | 'idDocumento' | 'nomeArquivo' | 'motivoRecusa'> {
  if (!enviado) return { status: 'pendente' };

  const status = STATUS_POR_ANALISE[enviado.tp_status_analise];
  return {
    status,
    idDocumento: enviado.id,
    nomeArquivo: enviado.nom_arquivo,
    motivoRecusa: status === 'recusado' ? enviado.ds_motivo_recusa || MENSAGEM_RECUSA_PADRAO : undefined,
  };
}

@Component({
  imports: [UploadDocModal, Steps],
  selector: 'app-upload-documento',
  styleUrl: './upload-documento.scss',
  templateUrl: './upload-documento.html',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class UploadDocumento implements OnInit {
  private readonly router = inject(Router);
  private readonly loaderService = inject(LoaderService);
  private readonly parametrizacaoService = inject(ParametrizacaoService);
  private readonly requerimentoService = inject(RequerimentoService);
  private readonly requerimentoState = inject(RequerimentoState);
  private readonly toastService = inject(ToastService);

  readonly stepAtual = signal(5);
  readonly totalSteps = signal(7);
  readonly situacao = signal<SituacaoDocumento[]>([]);
  /** Documentos em que a pessoa já abriu o upload; pendentes, ficam com a borda vermelha. */
  private readonly tentativas = signal<ReadonlySet<string>>(new Set());
  /**
   * Requerimento já enviado e pendente de documentação: a pessoa veio dos detalhes ("Reenviar documentos") só para
   * trocar o documento recusado, e volta para lá.
   */
  readonly reenvio = signal(false);

  /**
   * Busca no catálogo os tipos de documento e os envios atuais do requerimento; cada tipo vira um card com a situação
   * do seu envio (pendente quando ainda não foi enviado).
   */
  async ngOnInit(): Promise<void> {
    this.loaderService.showLoader('Carregando...');
    try {
      const requerimento = await this.requerimentoState.carregar();
      this.reenvio.set(requerimento.status !== 'R');
      const [tipos, enviados] = await Promise.all([
        this.parametrizacaoService.listarTiposDocumento(),
        this.requerimentoService.listarDocumentos(requerimento.id),
      ]);
      this.situacao.set(
        tipos.map((tipo) => ({
          idTipoDocumento: tipo.id,
          tipoDocumento: tipo.nome,
          nomeDocumento: tipo.nome_exibicao,
          ...situacaoDoEnvio(enviados.find((enviado) => enviado.id_doc_tipo === tipo.id)),
        })),
      );
    } catch (erro) {
      this.router.navigate([rotaDoErroRequerimento(erro) ?? ROTA_PAGINA_INDISPONIVEL]);
    } finally {
      this.loaderService.hideLoader();
    }
  }

  readonly documentos = computed<DocumentoCard[]>(() =>
    this.situacao().map(({ tipoDocumento, nomeDocumento, status, idDocumento, nomeArquivo, motivoRecusa }) => {
      const travado = !aceitaEnvio(status);

      return {
        tipoDocumento,
        nomeDocumento,
        ...(travado && { idStatus: STATUS_CARD[status as keyof typeof STATUS_CARD], nomeArquivo }),
        ...(status === 'recusado' && { motivoRecusa }),
        travado,
        pendente: status === 'pendente',
        semArquivo: status === 'recusado' || (status === 'pendente' && this.tentativas().has(tipoDocumento)),
        removivel: !this.reenvio() && idDocumento !== undefined,
      };
    }),
  );

  /** Libera o continuar quando todo documento foi enviado e não foi recusado (em análise ou concluído). */
  readonly podeContinuar = computed(() => this.situacao().every((doc) => !aceitaEnvio(doc.status)));

  /**
   * Registra que a pessoa abriu o upload do documento (clique no card). Se ela sair do modal sem
   * enviar o arquivo, o documento continua pendente e a borda fica vermelha.
   */
  onTentativaUpload(tipoDocumento: string): void {
    this.tentativas.update((tentativas) => new Set(tentativas).add(tipoDocumento));
  }

  /**
   * Envia o arquivo assim que a pessoa confirma no modal; o card passa a refletir o tp_status_analise
   * devolvido. Se o envio falhar, o documento continua como estava para a pessoa tentar de novo.
   */
  async onArquivoDocumento({ tipoDocumento, file }: ArquivoDocumento): Promise<void> {
    const doc = this.situacao().find((item) => item.tipoDocumento === tipoDocumento);
    if (!file || !doc || !aceitaEnvio(doc.status)) return;

    this.loaderService.showLoader('Enviando...');
    try {
      const requerimento = await this.requerimentoState.carregar();
      const { id, tp_status_analise, ds_motivo_recusa } = await this.requerimentoService.enviarDocumento(
        requerimento.id,
        doc.idTipoDocumento,
        file,
      );
      const status = STATUS_POR_ANALISE[tp_status_analise];
      this.atualizarDocumento(tipoDocumento, {
        status,
        idDocumento: id,
        nomeArquivo: file.name,
        motivoRecusa: status === 'recusado' ? ds_motivo_recusa || MENSAGEM_RECUSA_PADRAO : undefined,
      });
    } catch (erro) {
      this.tratarErro(erro, MENSAGEM_ERRO_ENVIAR);
    } finally {
      this.loaderService.hideLoader();
    }
  }

  /**
   * Remove o envio do documento (lixeira); o card volta a pendente, como antes do primeiro envio. Se a remoção falhar,
   * o documento continua como estava.
   */
  async onRemoverDocumento(tipoDocumento: string): Promise<void> {
    const idDocumento = this.situacao().find((item) => item.tipoDocumento === tipoDocumento)?.idDocumento;
    if (idDocumento === undefined || this.reenvio()) return;

    this.loaderService.showLoader('Removendo...');
    try {
      const requerimento = await this.requerimentoState.carregar();
      await this.requerimentoService.removerDocumento(requerimento.id, idDocumento);
      this.atualizarDocumento(tipoDocumento, {
        status: 'pendente',
        idDocumento: undefined,
        nomeArquivo: undefined,
        motivoRecusa: undefined,
      });
      this.tentativas.update((tentativas) => new Set([...tentativas].filter((tipo) => tipo !== tipoDocumento)));
    } catch (erro) {
      this.tratarErro(erro, MENSAGEM_ERRO_REMOVER);
    } finally {
      this.loaderService.hideLoader();
    }
  }

  onReturn(): void {
    this.router.navigate([this.reenvio() ? ROTA_DETALHES : '/requerimento-beneficio/beneficiarios']);
  }

  onSubmit(): void {
    if (!this.podeContinuar()) return;
    this.router.navigate([this.reenvio() ? ROTA_DETALHES : '/requerimento-beneficio/resumo']);
  }

  private tratarErro(erro: unknown, mensagemPadrao: string): void {
    const rota = rotaDoErroRequerimento(erro);
    if (rota) {
      this.router.navigate([rota]);
      return;
    }

    distribuirErrosDaApi(erro, {}, mensagemPadrao).forEach((mensagem) =>
      this.toastService.show(mensagem, 'error'),
    );
  }

  private atualizarDocumento(tipoDocumento: string, alteracao: Partial<SituacaoDocumento>): void {
    this.situacao.update((documentos) =>
      documentos.map((doc) => (doc.tipoDocumento === tipoDocumento ? { ...doc, ...alteracao } : doc)),
    );
  }
}
